import type { RecordModel } from 'pocketbase';

import {
  getPB,
  getUser,
  newLegacyId,
  pbDate,
  type User,
} from '../client';

const collabActivePaths = new Set<string>();

/** Cache public workspace id → PocketBase record id for yjs tables. */
const workspacePbIdCache = new Map<string, string>();

async function resolveWorkspacePbId(publicWorkspaceId: string): Promise<string> {
  const cached = workspacePbIdCache.get(publicWorkspaceId);
  if (cached) return cached;
  const pb = getPB();
  const row = await pb
    .collection('workspaces')
    .getFirstListItem(
      pb.filter('legacy_id = {:id} || id = {:id}', { id: publicWorkspaceId }),
    );
  workspacePbIdCache.set(publicWorkspaceId, row.id);
  return row.id;
}

export function collabPathKey(workspaceId: string, filePath: string): string {
  return `${workspaceId}:${filePath}`;
}

export function markCollabActive(workspaceId: string, filePath: string) {
  collabActivePaths.add(collabPathKey(workspaceId, filePath));
}

export function markCollabInactive(workspaceId: string, filePath: string) {
  collabActivePaths.delete(collabPathKey(workspaceId, filePath));
}

export function isCollabActive(workspaceId: string, filePath: string): boolean {
  return collabActivePaths.has(collabPathKey(workspaceId, filePath));
}

/** Cap sync updates loaded per note — full history can be huge post-migration. */
const MAX_YDOC_UPDATES = 200;

export async function loadFileYdocSnapshot(
  workspaceId: string,
  filePath: string,
) {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(workspaceId);

  let snap: RecordModel | null = null;
  try {
    snap = await pb
      .collection('workspace_file_ydoc_snapshot')
      .getFirstListItem(
        pb.filter('workspace = {:ws} && file_path = {:path}', {
          ws: wsPbId,
          path: filePath,
        }),
      );
  } catch {
    snap = null;
  }

  // Prefer a compact recent window over getFullList (timeouts / huge payloads).
  // Sort by -created when autodate exists; fall back if the collection lacks it.
  let updates: RecordModel[] = [];
  const updatesFilter = pb.filter(
    'workspace = {:ws} && file_path = {:path} && kind = "sync"',
    { ws: wsPbId, path: filePath },
  );
  try {
    let page;
    try {
      page = await pb.collection('workspace_file_ydoc_updates').getList(
        1,
        MAX_YDOC_UPDATES,
        {
          filter: updatesFilter,
          // Newest first, then reverse so apply order is chronological.
          sort: '-created',
        },
      );
    } catch {
      page = await pb.collection('workspace_file_ydoc_updates').getList(
        1,
        MAX_YDOC_UPDATES,
        { filter: updatesFilter },
      );
    }
    updates = [...page.items].reverse();
  } catch (e) {
    console.warn('Failed to list ydoc updates', e);
    updates = [];
  }

  const mapped = updates.map((u, index) => ({
    id: index + 1,
    update_b64: String(u.update_b64 ?? ''),
    created_at: pbDate(u.created ?? u.created_at),
    record_id: u.id as string,
  }));

  let updatedAtIso: string | null = snap
    ? pbDate(snap.updated_at ?? snap.updated)
    : null;
  for (const update of mapped) {
    if (
      update.created_at &&
      (updatedAtIso == null || update.created_at > updatedAtIso)
    ) {
      updatedAtIso = update.created_at;
    }
  }
  return {
    snapshotB64: snap ? ((snap.snapshot_b64 as string | null) ?? null) : null,
    updates: mapped,
    /** Latest durable Yjs activity (snapshot or sync update). */
    updatedAtMs: updatedAtIso ? Date.parse(updatedAtIso) : null,
    workspacePbId: wsPbId,
  };
}

export async function insertFileYdocUpdate(params: {
  workspaceId: string;
  filePath: string;
  updateB64: string;
  kind?: 'sync' | 'awareness';
  /** Optional; ignored by storage — used by the provider for echo suppression. */
  clientId?: string;
}) {
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(params.workspaceId);
  void params.clientId;
  await pb.collection('workspace_file_ydoc_updates').create({
    legacy_id: newLegacyId(),
    workspace: wsPbId,
    file_path: params.filePath,
    update_b64: params.updateB64,
    kind: params.kind ?? 'sync',
  });
}

export async function archiveFileYdocVersion(params: {
  workspaceId: string;
  filePath: string;
  snapshotB64: string;
  kind?: 'auto' | 'manual';
  label?: string | null;
}) {
  const user: User | null = await getUser();
  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(params.workspaceId);
  return pb.collection('workspace_file_ydoc_versions').create({
    legacy_id: newLegacyId(),
    workspace: wsPbId,
    file_path: params.filePath,
    snapshot_b64: params.snapshotB64,
    kind: params.kind ?? 'auto',
    label: params.label ?? null,
    created_by: user?.id ?? null,
  });
}

export async function upsertFileYdocSnapshot(params: {
  workspaceId: string;
  filePath: string;
  snapshotB64: string;
  archivePrevious?: boolean;
  previousSnapshotB64?: string | null;
}) {
  if (
    params.archivePrevious &&
    params.previousSnapshotB64 &&
    params.previousSnapshotB64 !== params.snapshotB64
  ) {
    try {
      await archiveFileYdocVersion({
        workspaceId: params.workspaceId,
        filePath: params.filePath,
        snapshotB64: params.previousSnapshotB64,
        kind: 'auto',
      });
    } catch (e) {
      console.warn('Failed to archive ydoc version', e);
    }
  }

  const pb = getPB();
  const wsPbId = await resolveWorkspacePbId(params.workspaceId);
  const updatedAt = new Date().toISOString();
  const pruneBefore = new Date(Date.now() - 60_000).toISOString();

  try {
    const existing = await pb
      .collection('workspace_file_ydoc_snapshot')
      .getFirstListItem(
        pb.filter('workspace = {:ws} && file_path = {:path}', {
          ws: wsPbId,
          path: params.filePath,
        }),
      );
    await pb.collection('workspace_file_ydoc_snapshot').update(existing.id, {
      snapshot_b64: params.snapshotB64,
      updated_at: updatedAt,
    });
  } catch {
    await pb.collection('workspace_file_ydoc_snapshot').create({
      legacy_id: newLegacyId(),
      workspace: wsPbId,
      file_path: params.filePath,
      snapshot_b64: params.snapshotB64,
      updated_at: updatedAt,
    });
  }

  // Prune awareness rows and old sync updates for this file.
  try {
    const stale = await pb
      .collection('workspace_file_ydoc_updates')
      .getFullList({
        filter: pb.filter(
          'workspace = {:ws} && file_path = {:path}',
          { ws: wsPbId, path: params.filePath },
        ),
      });
    for (const row of stale) {
      const kind = String(row.kind ?? 'sync');
      const created = pbDate(row.created ?? row.created_at);
      if (kind === 'awareness' || (kind === 'sync' && created < pruneBefore)) {
        await pb.collection('workspace_file_ydoc_updates').delete(row.id);
      }
    }
  } catch (e) {
    console.warn('Failed to prune ydoc updates', e);
  }
}

const YDOC_COLLECTIONS = [
  'workspace_file_ydoc_snapshot',
  'workspace_file_ydoc_updates',
  'workspace_file_ydoc_versions',
] as const;

/** PocketBase batch requests cap; keep under the server default (100). */
const YDOC_DELETE_BATCH = 50;

async function deleteRowsBatched(
  collection: string,
  ids: string[],
): Promise<void> {
  if (!ids.length) return;
  const pb = getPB();
  for (let i = 0; i < ids.length; i += YDOC_DELETE_BATCH) {
    const chunk = ids.slice(i, i + YDOC_DELETE_BATCH);
    const batch = pb.createBatch();
    for (const id of chunk) {
      batch.collection(collection).delete(id);
    }
    await batch.send();
  }
}

async function deleteYdocRowsForPath(
  wsPbId: string,
  filePath: string,
): Promise<void> {
  const pb = getPB();
  const filter = pb.filter('workspace = {:ws} && file_path = {:path}', {
    ws: wsPbId,
    path: filePath,
  });
  // List all three collections in parallel, then batch-delete in parallel.
  const listed = await Promise.all(
    YDOC_COLLECTIONS.map(async (collection) => {
      try {
        const rows = await pb.collection(collection).getFullList({ filter });
        return { collection, ids: rows.map((row) => row.id) };
      } catch (e) {
        console.warn(
          'Failed to list remote ydoc rows',
          { collection, filePath },
          e,
        );
        return { collection, ids: [] as string[] };
      }
    }),
  );
  await Promise.all(
    listed.map(async ({ collection, ids }) => {
      try {
        await deleteRowsBatched(collection, ids);
      } catch (e) {
        console.warn(
          'Failed to delete remote ydoc rows',
          { collection, filePath },
          e,
        );
      }
    }),
  );
}

/**
 * Drop every remote ydoc row for a path (snapshot, sync updates, versions).
 * Path-keyed collab must not outlive the note — otherwise recreating the same
 * name resurrects deleted content. Best-effort; empty paths return after a
 * cheap existence check so new-note create stays fast.
 */
export async function deleteFileYdoc(
  workspaceId: string,
  filePath: string,
): Promise<void> {
  let wsPbId: string;
  try {
    wsPbId = await resolveWorkspacePbId(workspaceId);
  } catch (e) {
    console.warn('Failed to resolve workspace for ydoc delete', filePath, e);
    return;
  }
  const pb = getPB();
  const filter = pb.filter('workspace = {:ws} && file_path = {:path}', {
    ws: wsPbId,
    path: filePath,
  });

  // Fast path: brand-new names have no collab history. Two tiny list probes
  // beat three full scans + delete round-trips on the create hot path.
  try {
    const [snap, updates] = await Promise.all([
      pb.collection('workspace_file_ydoc_snapshot').getList(1, 1, { filter }),
      pb.collection('workspace_file_ydoc_updates').getList(1, 1, { filter }),
    ]);
    if (snap.totalItems === 0 && updates.totalItems === 0) {
      const versions = await pb
        .collection('workspace_file_ydoc_versions')
        .getList(1, 1, { filter });
      if (versions.totalItems === 0) return;
    }
  } catch {
    // Probe failed — fall through to a full purge attempt.
  }

  await deleteYdocRowsForPath(wsPbId, filePath);
}

/**
 * Rekey remote ydoc rows when a note is renamed. Clears any clash at the
 * destination path first (unique on workspace + file_path for snapshots).
 */
export async function renameFileYdoc(
  workspaceId: string,
  oldPath: string,
  newPath: string,
): Promise<void> {
  if (oldPath === newPath) return;
  try {
    const pb = getPB();
    const wsPbId = await resolveWorkspacePbId(workspaceId);
    // Destination may hold orphaned ydoc from a prior delete — drop it so the
    // unique snapshot index and update/version rows do not collide.
    await deleteYdocRowsForPath(wsPbId, newPath);

    for (const collection of YDOC_COLLECTIONS) {
      const rows = await pb.collection(collection).getFullList({
        filter: pb.filter('workspace = {:ws} && file_path = {:path}', {
          ws: wsPbId,
          path: oldPath,
        }),
      });
      for (const row of rows) {
        await pb.collection(collection).update(row.id, { file_path: newPath });
      }
    }
  } catch (e) {
    console.warn(
      'Failed to rename remote ydoc',
      { oldPath, newPath },
      e,
    );
  }
}

/** Resolve PB workspace id for realtime filters (also used by the provider). */
export async function getYdocWorkspacePbId(
  workspaceId: string,
): Promise<string> {
  return resolveWorkspacePbId(workspaceId);
}
